// Regras de anexo do assistente de IA (Fase B — imagem; Fase C — áudio).
//
// Os limites aqui são uma PRÉ-validação de UX (feedback imediato antes do upload);
// a fonte da verdade é o funil de anexo do backend (AnexoFunnelService), que revalida
// tudo. Os números DEVEM ser mantidos em paridade com o backend (AssistenteAnexos em
// appsettings) e com o stack Angular (anexos.ts equivalente).

export type AnexoTipo = 'imagem' | 'audio';

export const MAX_ANEXOS = 4;
export const MAX_IMAGENS = 2; // máximo de imagens por mensagem
export const MAX_BYTES_IMAGEM = 5 * 1024 * 1024; // 5 MB
export const MAX_BYTES_AUDIO = 15 * 1024 * 1024; // 15 MB

export const MIME_IMAGEM_PERMITIDOS = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'] as const;
export const ACCEPT_IMAGEM = MIME_IMAGEM_PERMITIDOS.join(',');

/** MIME do áudio gravado pelo MediaRecorder (webm/opus) — paridade com o Angular. */
export const MIME_AUDIO_GRAVACAO = 'audio/webm';

/**
 * Anexo em composição: o arquivo escolhido/gravado + um object URL (thumbnail p/ imagem,
 * fonte de reprodução p/ áudio). Áudio carrega a duração para exibir no chip.
 */
export interface AnexoComposicao {
    id: number;
    tipo: AnexoTipo;
    file: File;
    url: string;
    duracaoSegundos?: number;
}

/** Anexo exibido na bolha do usuário (só metadados + object URL para preview local). */
export interface AnexoMensagem {
    tipo: AnexoTipo;
    mimeType: string;
    nomeArquivo: string;
    url: string;
    duracaoSegundos?: number;
}

/** Converte um File em base64 puro (sem o prefixo "data:...;base64,"). */
export function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const dataUrl = reader.result as string;
            const base64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;
            resolve(base64 || '');
        };
        reader.onerror = () => reject(new Error('Falha ao ler o arquivo.'));
        reader.readAsDataURL(file);
    });
}

/** Formata bytes de forma legível (ex.: "1.2 MB"). */
export function formatarTamanho(bytes: number): string {
    if (bytes < 1024) {
        return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
        return `${(bytes / 1024).toFixed(0)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Conta quantos anexos de imagem já estão em composição. */
export function contarImagens(anexos: AnexoComposicao[]): number {
    return anexos.filter((anexo) => anexo.tipo === 'imagem').length;
}

/** Formata uma duração em segundos como "m:ss". */
export function formatarDuracao(segundos: number): string {
    const total = Math.max(0, Math.round(segundos));
    const min = Math.floor(total / 60);
    const seg = total % 60;
    return `${min}:${seg.toString().padStart(2, '0')}`;
}

/**
 * Pré-valida um arquivo de imagem contra os limites técnicos. Retorna a mensagem
 * de erro (em português) ou null se válido.
 */
export function validarArquivoImagem(file: File): string | null {
    if (!MIME_IMAGEM_PERMITIDOS.includes(file.type as (typeof MIME_IMAGEM_PERMITIDOS)[number])) {
        return `"${file.name}": tipo não suportado. Use PNG, JPEG, WEBP ou GIF.`;
    }
    if (file.size > MAX_BYTES_IMAGEM) {
        return `"${file.name}": excede o limite de ${formatarTamanho(MAX_BYTES_IMAGEM)}.`;
    }
    return null;
}

/**
 * Pré-valida um áudio gravado contra o limite de tamanho. Retorna a mensagem de erro
 * (em português) ou null se válido.
 */
export function validarAudio(file: File): string | null {
    if (file.size > MAX_BYTES_AUDIO) {
        return `Áudio excede o limite de ${formatarTamanho(MAX_BYTES_AUDIO)}.`;
    }
    return null;
}
